import {Pivot, PivotItem, Separator, Stack} from '@fluentui/react';
import React, {useMemo} from 'react';
import {DetailsField} from './DetailsSection';
import {useTranslation} from 'react-i18next';

export interface TabsInitialFieldsProps {
	rows: DetailsField[][];
	renderField: (field: DetailsField, tooltipContent?: string) => JSX.Element;
	classnames?: {singleDetail?: string};
	generalFieldLabels?: string[];
	dateFieldLabels?: string[];
	referenceFieldLabels?: string[];
}

export const TabsInitialFields: React.FC<TabsInitialFieldsProps> = ({
	rows,
	renderField,
	classnames,
	generalFieldLabels = [],
	dateFieldLabels = [],
	referenceFieldLabels = [],
}) => {
	const {t} = useTranslation('components/detailssection');
	const allFields = useMemo(() => rows.flat(), [rows]);

	const labelSet = (labels: string[]) =>
		new Set(labels.map(x => x.trim()).filter(Boolean));

	const generalSet = useMemo(
		() => labelSet(generalFieldLabels),
		[generalFieldLabels],
	);
	const dateSet = useMemo(() => labelSet(dateFieldLabels), [dateFieldLabels]);
	const referenceSet = useMemo(
		() => labelSet(referenceFieldLabels),
		[referenceFieldLabels],
	);

	const categorizedTabFields = useMemo(() => {
		const general: DetailsField[] = [];
		const date: DetailsField[] = [];
		const reference: DetailsField[] = [];

		allFields.forEach(field => {
			const label = field.label?.trim();
			if (!label) return;

			if (generalSet.has(label)) general.push(field);
			if (dateSet.has(label)) date.push(field);
			if (referenceSet.has(label)) reference.push(field);
		});

		return {
			generalField: general,
			dateField: date,
			referenceField: reference,
		};
	}, [allFields, generalSet, dateSet, referenceSet]);

	// Split fields into 2-column rows
	const splitToColumns = (list: DetailsField[]) => {
		const result: DetailsField[][] = [];
		for (let i = 0; i < list.length; i += 2) {
			result.push(list.slice(i, i + 2));
		}
		return result;
	};

	// Render each tab’s content
	const renderTabContent = (fields: DetailsField[]) => {
		const rows = splitToColumns(fields);

		return rows.map((row, ri) => (
			<div key={ri}>
				<Stack horizontal tokens={{childrenGap: 50}}>
					{row.map(field => (
						<div
							key={field.label}
							className={classnames?.singleDetail}
							style={classnames?.singleDetail ? undefined : {width: '50%'}}
						>
							{renderField(field, field.tooltipContent)}
						</div>
					))}
				</Stack>
				<Separator />
			</div>
		));
	};

	return (
		<Pivot>
			<PivotItem itemKey='GeneralTab' headerText={t('GeneralTabHeader')}>
				{renderTabContent(categorizedTabFields.generalField)}
			</PivotItem>

			<PivotItem itemKey='DateTab' headerText={t('DateTabHeader')}>
				{renderTabContent(categorizedTabFields.dateField)}
			</PivotItem>

			<PivotItem itemKey='ReferenceTab' headerText={t('ReferenceTabHeader')}>
				{renderTabContent(categorizedTabFields.referenceField)}
			</PivotItem>
		</Pivot>
	);
};
